import React from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Formik } from 'formik';
import { cloneDeep, get } from 'lodash';

import mixpanel from '~/components/CmsMain/mixpanel';
import { LoadingSwitch } from '~/components/core';
import useCurrentUserRoles from '~/components/hooks/useCurrentUserRoles';
import {
  CliveConfigurationOptionsContext,
  useCliveConfigOptions,
} from '~/components/SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import TriageSection from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/Sections/TriageSection';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import useDataFetcher from '~/components/useDataFetcher';
import { FNOL_MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isFeatureEnabled, reportAxiosError } from '~/Utils';

import SectionWrapper from '../../../Fnol/NewFnolUI/SectionWrapper';
import ConfigurationBreadcrumbs from '../../IncidentConfigurationPage/ConfigurationBreadcrumbs';
import { stripDraftIdPrefix } from '../../IncidentConfigurationPage/draftName';
import GlobalFieldsDriftBanner from '../../IncidentConfigurationPage/GlobalFieldsDriftBanner';
import StaleDraftsConfirmModal from '../../IncidentConfigurationPage/StaleDraftsConfirmModal';
import { useStaleDraftsSaveGuard } from '../../IncidentConfigurationPage/useStaleDraftsSaveGuard';
import { setContactConfiguredFieldsKeysInValues } from '../../IncidentConfigurationPage/utils';
import fnolConfigurationSchema from '../fnolConfigurationValidationSchema';
import setOnTheFlyFnolConfigurationMigrations, { removeStaticFields } from '../onTheFlyConfigurationMigrations';

import FnolPrompt from './FnolPrompt/FnolPrompt';
import Header from './Header/Header';
import InvolvedDriverSection from './Sections/AutoInvolvedSections/InvolvedDriverSection';
import InvolvedNonMotoristSection from './Sections/AutoInvolvedSections/InvolvedNonMotoristSection';
import InvolvedPassengerSection from './Sections/AutoInvolvedSections/InvolvedPassengerSection';
import InvolvedPropertySection from './Sections/AutoInvolvedSections/InvolvedPropertySection';
import InvolvedVehicleSection from './Sections/AutoInvolvedSections/InvolvedVehicleSection';
import ExternalClaimNumberConfigSection from './Sections/ExternalClaimNumberConfigSection';
import FilesSection from './Sections/FilesSection';
import GeneralDetailsSection from './Sections/GeneralDetailsSection';
import GeneralInvolvedPartySection from './Sections/GeneralInvolvedPartySection';
import IncidentDetailsSection from './Sections/IncidentDetailsSection';
import IncidentTypesAndSubTypesSection from './Sections/IncidentTypesAndSubTypesSection/IncidentTypesAndSubTypesSection';
import WitnessesSection from './Sections/WitnessesSection';
import FnolConfigurationFooter from './FnolConfigurationFooter';
import FnolConfigurationSectionsPanel from './FnolConfigurationSectionsPanel';
import { PolicyCategoriesOptionsProvider, useFetchPolicyCategories } from './PolicyCategoriesOptions';

export const getInvolvedPartiesSections = (fnolConfiguration) => {
  if (fnolConfiguration?.lob === 'auto_claim') {
    return [
      {
        id: 'involved_parties',
        title: 'Involved Parties',
        // Nav entry stays collapsible; the main-panel body renders as a non-collapsible band.
        isCollapsible: true,
        renderBodyAsBand: true,
        children: [
          {
            id: 'involved_vehicle',
            title: 'Involved Vehicle',
            node: <InvolvedVehicleSection />,
          },
          {
            id: 'involved_driver',
            title: 'Involved Driver',
            node: <InvolvedDriverSection />,
          },
          {
            id: 'involved_passenger',
            title: 'Involved Passenger',
            node: <InvolvedPassengerSection />,
          },
          {
            id: 'involved_non_motorist',
            title: 'Involved Non Motorist',
            node: <InvolvedNonMotoristSection />,
          },
          {
            id: 'involved_generic_property',
            title: 'Involved Property',
            node: <InvolvedPropertySection />,
          },
        ],
      },
    ];
  }

  const involvedParties = get(fnolConfiguration, 'configuration.involved_parties', {});
  const sortedInvolvedKeys = Object.keys(involvedParties)?.sort((a, b) =>
    involvedParties[a]?.is_first_party === involvedParties[b].is_first_party
      ? 0
      : involvedParties[a].is_first_party
        ? -1
        : 1
  );

  return [
    {
      id: 'involved_parties',
      title: 'Involved Parties',
      // Nav entry stays collapsible; the main-panel body renders as a non-collapsible band.
      isCollapsible: true,
      renderBodyAsBand: true,
      children: sortedInvolvedKeys.map((partyKey) => {
        const party = involvedParties[partyKey];
        const titlePrefix = party?.is_first_party ? 'First Party' : 'Third Party';
        const title = `${titlePrefix} - ${party?.desc}`;
        return {
          id: partyKey,
          title,
          node: <GeneralInvolvedPartySection partyKey={partyKey} />,
        };
      }),
    },
  ];
};

/** @type {React.FC<import('../../IncidentConfigurationPage/types').ConfigurationScreenProps>} */
const FnolConfigurationScreen = ({
  incidentConfigurationId,
  subOrgId,
  onCancel,
  onSubmit,
  hasActiveInbox = false,
  isInternalAdmin = false,
  draftId,
}) => {
  const { organization } = useSysconfig();
  const { isUserFromSuperOrganization } = useCurrentUserRoles();
  const isSaveDraftEnabled = isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.INCIDENT_CONFIGURATION_DRAFTS);
  const isCustomSectionsEnabled = isFeatureEnabled(
    organization,
    CONFIGURATION_FEATURES_NAMES.CUSTOM_FNOL_ADDITIONAL_INFORMATION_SECTIONS
  );
  // The "Save as draft" button is internal-admin only and gated on the logged-in
  // operator belonging to the super org - customers never see it.
  const showDraftsUi = isInternalAdmin && isUserFromSuperOrganization;
  const cliveContext = useCliveConfigOptions();
  const effectiveShowClive = cliveContext.showCliveConfigOptions && hasActiveInbox !== false;

  const incidentConfigurationsRoute = `/sysconfig/api/v1/organizations/${organization.id}/incident_configurations/${incidentConfigurationId}`;
  const {
    isLoading,
    isError,
    data: fnolConfiguration = {},
    reloadData: reloadConfiguration,
  } = useDataFetcher(`${incidentConfigurationsRoute}`, {});

  // Gated in the LoadingSwitch below, not consumed lazily: the on-the-fly category migration
  // needs the policy keys in the very first `initialValues`, and `enableReinitialize` would
  // otherwise discard in-progress edits the moment the fetch resolved.
  const {
    options: policyCategoryOptions,
    keys: policyCategoryKeys,
    isLoading: arePolicyCategoriesLoading,
    isError: havePolicyCategoriesErrored,
  } = useFetchPolicyCategories(incidentConfigurationId);

  // When opened from a saved draft, load it and overlay its configuration onto the live
  // envelope so the editor continues from the draft (version stays the live one, so a
  // subsequent save-as-draft / save is checked against the current live config).
  const draftRoute = `/sysconfig/api/v1/organizations/${organization.id}/incident_configuration_drafts/${draftId}`;
  const {
    isLoading: isDraftLoading,
    isError: isDraftError,
    data: draft,
  } = useDataFetcher(draftRoute, {}, { shouldFetch: !!draftId });

  // A draft's stored name carries the "#<draft id> " prefix the drafts list shows it under.
  // The editor puts this name into display_name, which both a re-save and a live Save write
  // back, so the prefix is dropped here rather than accumulating / reaching the live config.
  // display_name is required, and a draft saved without a name strips to nothing, so the live
  // configuration's name still stands in - leaving it empty would block the live save.
  const initialConfiguration =
    draftId && draft
      ? {
          ...fnolConfiguration,
          configuration: draft.configuration,
          display_name: stripDraftIdPrefix(draft.id, draft.name) || fnolConfiguration.display_name,
        }
      : fnolConfiguration;

  const isScreenLoading = isLoading || (!!draftId && isDraftLoading) || arePolicyCategoriesLoading;
  const isScreenError = isError || (!!draftId && isDraftError) || havePolicyCategoriesErrored;
  // Built only once every fetch has landed, so the migration never runs against a loaded configuration
  // before its policy keys arrive. The LoadingSwitch keeps the form unmounted until then, so the result
  // of any earlier run would only be discarded.
  const initialValues =
    isScreenLoading || isScreenError
      ? undefined
      : setOnTheFlyFnolConfigurationMigrations(
          { ...initialConfiguration, sub_organization_id: subOrgId },
          isCustomSectionsEnabled,
          policyCategoryKeys
        );

  const trackMixpanelEvent = () => {
    mixpanel.track(FNOL_MIXPANEL_EVENTS.SAVE_FNOL_CONFIGURATION_CLICKED, {
      incident_configuration_id: incidentConfigurationId,
      sub_organization_id: subOrgId,
    });
  };

  const handleSaveDraft = async (values) => {
    try {
      const preparedValues = setContactConfiguredFieldsKeysInValues(removeStaticFields(cloneDeep(values)));
      await axios.post(`${incidentConfigurationsRoute}/drafts`, {
        name: preparedValues.display_name,
        configuration: preparedValues.configuration,
        version: fnolConfiguration.version,
        sub_organization_id: subOrgId,
      });
      toast.success('FNOL Configuration draft saved');
    } catch (error) {
      await reportAxiosError(error);
    }
  };

  const saveFnolConfiguration = async (values) => {
    trackMixpanelEvent();
    values = setContactConfiguredFieldsKeysInValues(values);
    await axios.patch(incidentConfigurationsRoute, {
      old_configuration: fnolConfiguration,
      new_configuration: values,
    });
    await onSubmit();
  };

  const { guardedSave, confirmModalProps } = useStaleDraftsSaveGuard({
    isEnabled: isSaveDraftEnabled,
    organizationId: organization.id,
    onSave: saveFnolConfiguration,
  });

  return (
    <LoadingSwitch isLoading={isScreenLoading} isError={isScreenError}>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={fnolConfigurationSchema(fnolConfiguration)}
        onSubmit={async (values, formikProps) => {
          const { setSubmitting } = formikProps;
          try {
            setSubmitting(true);
            // Strip on a copy, as "save as draft" already does. `removeStaticFields` unsets in
            // place, and `setOnTheFlyFnolConfigurationMigrations` puts the static witnesses fields
            // straight back on every render - so a payload held across the stale-drafts
            // confirmation would be re-populated and rejected by the server, which forbids them.
            values = removeStaticFields(cloneDeep(values));
            await guardedSave(values, {
              setSubmitting,
              incidentConfigurationId,
              liveVersion: fnolConfiguration.version,
              excludedDraftId: draftId,
            });
          } catch (error) {
            setSubmitting(false);
          }
        }}
      >
        <PolicyCategoriesOptionsProvider options={policyCategoryOptions}>
          <CliveConfigurationOptionsContext.Provider
            value={{ ...cliveContext, showCliveConfigOptions: effectiveShowClive }}
          >
            <FnolPrompt />
            <StaleDraftsConfirmModal {...confirmModalProps} />
            <div className="fixed z-10 w-full bg-slate-200 p-12">
              <ConfigurationBreadcrumbs
                configurationName="FNOL Configuration"
                externalAdminRoute="/fnol_configuration"
                onCancel={onCancel}
              />
            </div>
            {/* Gap between start of sections */}
            {Array.from(Array(2).keys()).map((item, index) => (
              <SectionWrapper key={index} />
            ))}
            <Header version={fnolConfiguration.version} />
            <GlobalFieldsDriftBanner
              globalFieldsStatus={fnolConfiguration.global_fields_status}
              incidentConfigurationRoute={incidentConfigurationsRoute}
              reloadConfiguration={reloadConfiguration}
            />
            <div className="bg-slate-200 pt-32">
              <div>
                <FnolConfigurationSectionsPanel
                  fnolConfiguration={fnolConfiguration}
                  isAddSectionEnabled={isCustomSectionsEnabled}
                  sectionsBefore={[
                    {
                      id: 'general_details',
                      title: 'General Details',
                      node: <GeneralDetailsSection />,
                    },
                    {
                      id: 'incident_types',
                      title: 'Incident Types & Sub-Types',
                      node: <IncidentTypesAndSubTypesSection />,
                    },
                    {
                      id: 'incident_details',
                      title: 'Incident Details',
                      node: <IncidentDetailsSection />,
                    },
                    ...getInvolvedPartiesSections(fnolConfiguration),
                  ]}
                  sectionsAfter={[
                    { id: 'witnesses', title: 'Witnesses', node: <WitnessesSection /> },
                    {
                      id: 'external_claim_numbers',
                      title: 'External Claim Numbers',
                      node: <ExternalClaimNumberConfigSection />,
                    },
                    { id: 'triage', title: 'Triage', node: <TriageSection /> },
                    { id: 'documents', title: 'Files', node: <FilesSection /> },
                  ]}
                  panelTitle="FNOL Content"
                  SectionWrapperComponent={SectionWrapper}
                  sectionContainerClassName="scroll-mt-40"
                  handleScrollTopOffset={40}
                />
                {/* Gap between end of sections and the footer */}
                {Array.from(Array(16).keys()).map((item, index) => (
                  <SectionWrapper key={index} />
                ))}
              </div>
            </div>
            <FnolConfigurationFooter
              onCancel={onCancel}
              onSaveDraft={handleSaveDraft}
              showSaveDraft={showDraftsUi && isSaveDraftEnabled}
            />
          </CliveConfigurationOptionsContext.Provider>
        </PolicyCategoriesOptionsProvider>
      </Formik>
    </LoadingSwitch>
  );
};

FnolConfigurationScreen.propTypes = {
  incidentConfigurationId: PropTypes.number.isRequired,
  subOrgId: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  hasActiveInbox: PropTypes.bool,
  isInternalAdmin: PropTypes.bool,
  draftId: PropTypes.number,
};

export default FnolConfigurationScreen;
